import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sltAPI } from '../../services/api';
import SLTLayout from './SLTLayout';
import { BRAND, cardStyle, TagBadge, formatDate, EmptyState, StudentActivityFeed } from './sltUi';

// Primary KPIs get large cards; secondary volume metrics sit in a compact strip.
const PrimaryKpi = ({ label, value, sub, accent }) => (
  <div style={{ ...cardStyle, borderTop: `6px solid ${accent}`, minWidth: 0 }}>
    <div style={{ color: BRAND.muted, fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</div>
    <div style={{ fontFamily: 'Nunito, sans-serif', fontSize: '2.3rem', fontWeight: 800, lineHeight: 1.1, marginTop: '6px' }}>{value ?? '—'}</div>
    {sub && <div style={{ color: BRAND.muted, fontSize: '0.82rem', marginTop: '4px' }}>{sub}</div>}
  </div>
);

const SLTOverview = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    sltAPI.getOverview()
      .then((res) => { if (isMounted) setOverview(res.data); })
      .catch(() => { if (isMounted) setError('Unable to load the school overview. Please try again.'); });
    return () => { isMounted = false; };
  }, []);

  const totals = overview?.totals || {};
  const performance = overview?.performance || {};
  const activity = overview?.recentActivity || [];
  const studentActivity = overview?.recentStudentActivity || [];

  return (
    <SLTLayout
      user={user}
      onLogout={onLogout}
      title="School overview"
      subtitle="Headline engagement and performance across every teacher and student in your school."
    >
      {error && <p style={{ color: '#B3261E', fontWeight: 700 }}>{error}</p>}
      {!overview && !error && <p style={{ color: BRAND.muted }}>Loading school data…</p>}

      {overview && (
        <>
          <section aria-label="Key metrics" style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', marginBottom: '16px' }}>
            <PrimaryKpi label="Students" value={totals.students} sub={`across ${totals.classes ?? 0} classes`} accent={BRAND.coral} />
            <PrimaryKpi label="Teachers" value={totals.teachers} sub={`${totals.lessonPlans ?? 0} lesson plans created`} accent={BRAND.teal} />
            <PrimaryKpi
              label="Average accuracy"
              value={performance.averageAccuracy != null ? `${performance.averageAccuracy}%` : '—'}
              sub="correct answers across all attempts"
              accent="#1F7A3A"
            />
            <PrimaryKpi
              label="Tutorial completion"
              value={performance.tutorialCompletionRate != null ? `${performance.tutorialCompletionRate}%` : '—'}
              sub={`${totals.tutorialsGenerated ?? 0} tutorials generated`}
              accent="#1F7A3A"
            />
          </section>

          <section aria-label="Volume metrics" style={{ ...cardStyle, display: 'flex', flexWrap: 'wrap', gap: '8px 34px', marginBottom: '26px', padding: '14px 20px' }}>
            {[
              ['Lesson plans', totals.lessonPlans],
              ['Homeworks published', totals.homeworks],
              ['Tutorials generated', totals.tutorialsGenerated],
              ['Attempts recorded', totals.attempts],
              ['Attempt completion', performance.attemptCompletionRate != null ? `${performance.attemptCompletionRate}%` : '—']
            ].map(([label, value]) => (
              <div key={label} style={{ fontSize: '0.9rem' }}>
                <span style={{ color: BRAND.muted, fontWeight: 700 }}>{label}: </span>
                <span style={{ fontWeight: 800 }}>{value ?? '—'}</span>
              </div>
            ))}
          </section>

          <section style={{ ...cardStyle, marginBottom: '26px' }}>
            <h2 style={{ fontSize: '1.25rem', margin: '0 0 4px' }}>Recent teacher activity</h2>
            <p style={{ color: BRAND.muted, fontSize: '0.88rem', margin: '0 0 12px' }}>The latest homework published by your teachers. Click a row to open the teacher's profile.</p>
            {activity.length === 0 && <EmptyState title="No homework published yet" hint="Activity will appear here as soon as teachers publish homework." />}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {activity.map((item) => (
                <div
                  key={item.homeworkId}
                  className="slt-row clickable"
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(`/slt/teachers/${item.teacherUid}`)}
                  onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/slt/teachers/${item.teacherUid}`); }}
                  style={{ alignItems: 'baseline', borderBottom: '1px solid rgba(45,64,66,0.1)', display: 'flex', flexWrap: 'wrap', gap: '10px', padding: '12px 6px' }}
                >
                  <strong style={{ fontFamily: 'Nunito, sans-serif' }}>{item.teacherName}</strong>
                  <span style={{ color: BRAND.muted }}>published</span>
                  <span style={{ fontWeight: 700 }}>{item.lessonPlanTitle}</span>
                  <TagBadge>{item.label}</TagBadge>
                  {item.className && <TagBadge tone="coral">{item.className}{item.yearGroup ? ` · ${item.yearGroup}` : ''}</TagBadge>}
                  <span style={{ color: BRAND.muted, fontSize: '0.85rem', marginLeft: 'auto' }}>{formatDate(item.createdAt)}</span>
                </div>
              ))}
            </div>
          </section>

          <section style={cardStyle}>
            <h2 style={{ fontSize: '1.25rem', margin: '0 0 4px' }}>Recent student activity</h2>
            <p style={{ color: BRAND.muted, fontSize: '0.88rem', margin: '0 0 12px' }}>The latest digital tutorials taken by students. Click a row to open the student's profile.</p>
            <StudentActivityFeed items={studentActivity} />
          </section>
        </>
      )}
    </SLTLayout>
  );
};

export default SLTOverview;
